// May 2026 monthly report document.
import { defineDocument, em, link, list, p, section, table, text, } from '../../schema';
export const report202605TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-05-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-05-top-party-games-and-streamers',
    route: '/party-central/reports/2026-05-top-party-games-and-streamers',
    seo: {
        title: 'May 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for May 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'May 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'sky',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-06-02T00:00:00.000Z'),
        updatedAt: new Date('2026-06-02T00:00:00.000Z'),
        reportMonth: '2026-05',
        dataRange: {
            start: '2026-05-01',
            end: '2026-05-31',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('In May we tracked 185,423 party game streams from 73,957 streamers across 829 games and 35 languages. See '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' for collection details. Compared with April, the month was smaller even after accounting for the extra day: streams fell 5.3% per day and unique streamers fell 10.2% per day.'),
                ]),
                p([
                    em('nanos world'),
                    text(' stayed at the top of Game Party Score because its French-centered community became even more repeat-heavy. Its stream count rose from 2,127 to 2,794, and stickiness increased from 10.6 to 13.1 streams per streamer.'),
                ]),
                p([
                    text('The most widely streamed games still looked familiar: '),
                    em('Among Us'),
                    text(', '),
                    em('Fall Guys'),
                    text(', and '),
                    em('Lethal Company'),
                    text(' held the three largest streamer bases. Underneath the surface we saw '),
                    em('Just Dance 2026 Edition'),
                    text(' climb to #3 by Party Score as streams, streamers, and average viewers all improved from April, while '),
                    em('Mario Kart World'),
                    text(' cooled from its prior top-three position.'),
                ]),
                p([
                    em('The House Always Wins'),
                    text(' launched on May 21 and we saw it streaming more than 500 times, with many streamers giving it multiple tries during its debut week. Three other new releases, including '),
                    em('Bow-wow Battle'),
                    text(', had successful launches with more than 100 streams during the month.'),
                ]),
                p([
                    link('https://twitch.tv/piapiufo', 'PiaPiUFO'),
                    text(' led Streamer Party Score after turning '),
                    em('Words On Stream'),
                    text(' into a frequent May habit: 42 of their 47 captures came there, then the channel added one-off looks at '),
                    em('Sonic Racing: CrossWorlds'),
                    text(', '),
                    em('Oh Deer'),
                    text(', '),
                    em('LEGO Party!'),
                    text(', '),
                    em('Pummel Party'),
                    text(', and '),
                    em('Mario Kart World'),
                    text('.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(" stayed first because its April breakout hardened into repeat behavior. Streams rose from 2,127 to 2,794 and stickiness jumped from 10.6 to 13.1. The French community's dedicated play of "),
                            em('Zenkai'),
                            text(', a game server on the platform, kept it at the top.'),
                        ]),
                        p([
                            text("The prior month's collapse in activity for "),
                            em('Diablo II: Resurrected'),
                            text(' came to a halt with May streams basically flat with April. '),
                            em('Jackbox Party Packs'),
                            text(' rose from #13 to #9 as most of its metrics improved slightly. '),
                            em('Just Dance 2026 Edition'),
                            text(' was the most notable riser, moving from #9 to #3 as streams rose 33.9% and average viewers rose 38.1%. '),
                        ]),
                        p([
                            em('Granblue Fantasy Versus: Rising'),
                            text(' is a new entrant on the list worth watching: it appeared at #11 after missing April, with big streams contributed by '),
                            link('https://twitch.tv/evo', 'Evo'),
                            text(' and '),
                            link('https://twitch.tv/tampaneversleeps', 'TampaNeverSleeps'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'nanos world', '95', '2,794', '213', '13.1', '56'],
                                ['2', 'Words On Stream', '92', '7,607', '656', '11.6', '19'],
                                [
                                    '3',
                                    'Just Dance 2026 Edition',
                                    '92',
                                    '663',
                                    '247',
                                    '2.7',
                                    '58',
                                ],
                                [
                                    '4',
                                    'Super Smash Bros. Ultimate',
                                    '91',
                                    '13,901',
                                    '3,763',
                                    '3.7',
                                    '18',
                                ],
                                [
                                    '5',
                                    'Diablo II: Resurrected',
                                    '91',
                                    '8,913',
                                    '1,576',
                                    '5.7',
                                    '17',
                                ],
                                ['6', 'Fall Guys', '90', '13,654', '6,092', '2.2', '20'],
                                ['7', 'Mario Kart 64', '90', '274', '109', '2.5', '42'],
                                [
                                    '8',
                                    'Mario Kart World',
                                    '89',
                                    '5,817',
                                    '2,321',
                                    '2.5',
                                    '18',
                                ],
                                [
                                    '9',
                                    'Jackbox Party Packs',
                                    '89',
                                    '4,479',
                                    '2,064',
                                    '2.2',
                                    '19',
                                ],
                                ['10', 'Mahjong Soul', '89', '4,306', '1,541', '2.8', '17'],
                                [
                                    '11',
                                    'Granblue Fantasy Versus: Rising',
                                    '89',
                                    '1,118',
                                    '428',
                                    '2.6',
                                    '21',
                                ],
                                ['12', 'Pokémon Stadium', '89', '250', '94', '2.7', '37'],
                                [
                                    '13',
                                    'The Legend of Heroes: Trails of Cold Steel',
                                    '89',
                                    '227',
                                    '57',
                                    '4.0',
                                    '36',
                                ],
                                ['14', 'Gartic on Stream', '89', '532', '51', '10.4', '34'],
                                ['15', 'Casino', '89', '118', '41', '2.9', '195'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/piapiufo', 'PiaPiUFO'),
                            text(' rose from #7 to #1 because capture frequency nearly quadrupled, from 12 to 47, while average viewers continued to exceed 500. '),
                            link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                            text(' has appeared in every available 2026 Party Score report so far and other mainstays like '),
                            link('https://twitch.tv/nohoho12', 'のほほ'),
                            text(' and '),
                            link('https://twitch.tv/astarriapple', 'AstarriApple'),
                            text(' return to the list again. '),
                        ]),
                        p([
                            link('https://twitch.tv/chilledchaos', 'ChilledChaos'),
                            text(' had the biggest audience among the top 10 while playing eight games. Two of their favorites were '),
                            em('Among Us'),
                            text(' and '),
                            em('Jackbox Party Packs'),
                            text('. '),
                            link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                            text(' is a German stream with high frequency and variety of games: 75 captures across 10 games, led by '),
                            em('Mario Kart 8 Deluxe'),
                            text(' and '),
                            em('Golf With Your Friends'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/piapiufo', 'PiaPiUFO')],
                                    '99',
                                    '47',
                                    '560',
                                    '6',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '99',
                                    '42',
                                    '213',
                                    '6',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '98',
                                    '20',
                                    '335',
                                    '4',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/sliverrk', 'SliverRK')],
                                    '98',
                                    '50',
                                    '162',
                                    '4',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/chilledchaos', 'ChilledChaos')],
                                    '97',
                                    '12',
                                    '3,191',
                                    '8',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/yousayix', 'yousayIX')],
                                    '97',
                                    '13',
                                    '542',
                                    '4',
                                ],
                                [
                                    '7',
                                    [
                                        link('https://twitch.tv/xmarksthespot26', 'XmarkstheSpot26'),
                                    ],
                                    '97',
                                    '13',
                                    '295',
                                    '8',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/francesconigris', 'francesconigris'),
                                    ],
                                    '97',
                                    '18',
                                    '182',
                                    '4',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '97',
                                    '21',
                                    '174',
                                    '4',
                                ],
                                [
                                    '10',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '97',
                                    '75',
                                    '70',
                                    '10',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/bebahan', 'Bebahan')],
                                    '96',
                                    '10',
                                    '1,265',
                                    '4',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                    '96',
                                    '22',
                                    '921',
                                    '2',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/dooleynoted', 'DooleyNoted')],
                                    '96',
                                    '10',
                                    '871',
                                    '4',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/sarahkey', 'SarahKey')],
                                    '96',
                                    '19',
                                    '505',
                                    '2',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/hypnoshark', 'hypnoshark')],
                                    '96',
                                    '11',
                                    '367',
                                    '6',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('May streaming activity of '),
                            em('Super Battle Golf'),
                            text(' cooled off in a major way with nearly 4,000 less streamers month-over-month and a 49% drop in total streams. The game still averaged 36 viewers per stream, up from 26 in April, with high-audience help from '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(', '),
                            link('https://twitch.tv/rdcgaming', 'RDCgaming'),
                            text(', and '),
                            link('https://twitch.tv/zentreya', 'Zentreya'),
                            text('. '),
                        ]),
                        p([
                            em('Lethal Company'),
                            text(" also fell with a 33% drop in streams from last month as the hype from April's game update faded."),
                        ]),
                        p([
                            em('Among Us'),
                            text(', '),
                            em('Super Smash Bros. Ultimate'),
                            text(', and '),
                            em('Fall Guys'),
                            text(' held their positions as some of the most streamed games.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Among Us', '14,146', '8,270', '19'],
                                ['2', 'Super Smash Bros. Ultimate', '13,901', '3,763', '18'],
                                ['3', 'Fall Guys', '13,654', '6,092', '20'],
                                ['4', 'Lethal Company', '9,819', '5,612', '18'],
                                ['5', 'Diablo II: Resurrected', '8,913', '1,576', '17'],
                                ['6', 'Words On Stream', '7,607', '656', '19'],
                                ['7', 'Super Battle Golf', '6,577', '4,954', '36'],
                                ['8', 'Mario Kart World', '5,817', '2,321', '18'],
                                ['9', 'Sonic Racing: CrossWorlds', '5,230', '2,234', '8'],
                                ['10', 'Gartic Phone', '5,131', '2,876', '25'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Jinsei Game for Nintendo Switch'),
                            text(' led on average viewers, but it was not broad audience adoption. Only seven streamers produced the minimum required 10 streams, and one popular Japanese creator, '),
                            link('https://twitch.tv/batora324', 'バトラ'),
                            text(', supplied 80% of the viewers.'),
                        ]),
                        p([
                            em('Unrailed!'),
                            text(' reached #6 because '),
                            link('https://twitch.tv/kato_junichi0817', '加藤純一うん〇ちゃん'),
                            text(' averaged 46,994 viewers across two captures. '),
                            em('Bow-wow Battle'),
                            text(', released in May, got a boost from the Spanish streaming community and especially '),
                            link('https://twitch.tv/rubius', 'Rubius'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Jinsei Game for Nintendo Switch', '1,227', '10', '7'],
                                ['2', 'Super Monkey Ball', '759', '36', '24'],
                                ['3', 'Family Feud', '734', '55', '41'],
                                ['4', 'Maitre des Fleurs', '688', '23', '15'],
                                ['5', 'Suika Game', '407', '93', '61'],
                                ['6', 'Unrailed!', '403', '240', '128'],
                                ['7', 'Rhythm Heaven Fever', '349', '101', '64'],
                                ['8', 'Bow-wow Battle', '236', '192', '183'],
                                ['9', 'Casino', '195', '118', '41'],
                                ['10', 'Lycans', '189', '200', '106'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Among Us'),
                            text(' was steady at the top, again surpassing 8,000 streamers this month. The biggest story in May is the precipitous drop of '),
                            em('Super Battle Golf'),
                            text(' and '),
                            em('Lethal Company'),
                            text(', both of which lost thousands of streamers, and slid down the rankings.'),
                        ]),
                        p([
                            text('On the flip side, '),
                            em('Sonic Racing: CrossWorlds'),
                            text(' picked up some of the slack. It added 420 streamers and more than 1,400 streams from last month, with a particularly big spike in the last few days of May.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'Among Us', '8,270', '14,146'],
                                ['2', 'Fall Guys', '6,092', '13,654'],
                                ['3', 'Lethal Company', '5,612', '9,819'],
                                ['4', 'Super Battle Golf', '4,954', '6,577'],
                                ['5', 'Super Smash Bros. Ultimate', '3,763', '13,901'],
                                ['6', 'Gartic Phone', '2,876', '5,131'],
                                ['7', 'Golf With Your Friends', '2,334', '3,673'],
                                ['8', 'Mario Kart World', '2,321', '5,817'],
                                ['9', 'Sonic Racing: CrossWorlds', '2,234', '5,230'],
                                ['10', 'Jackbox Party Packs', '2,064', '4,479'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(" became the stickiest game we've seen so far in the 2026 reports, rising from 10.6 streams per streamer in April to 13.1 in May. The repeat pattern came from a concentrated French group led by "),
                            link('https://twitch.tv/miidouw_', 'Miidouw_'),
                            text(', '),
                            link('https://twitch.tv/walidoen17', 'walidoen17'),
                            text(', '),
                            link('https://twitch.tv/queenihtv', 'QueenihTV'),
                            text(', '),
                            link('https://twitch.tv/wamoun', 'Wamoun'),
                            text(', and '),
                            link('https://twitch.tv/adamchico_', 'AdamChico_'),
                            text('.'),
                        ]),
                        p([
                            em('Words On Stream'),
                            text(' continues to be a stickiness juggernaut and has now been ranked #2 for the fourth month in a row. Notably, '),
                            em('Ragnarok X: Next Generation'),
                            text(' has also been in the top 5 for the past four months. '),
                            em('Scrabble'),
                            text(' and '),
                            em('Drawful 2'),
                            text(' earn honorable mentions for both clearing a score of 10+ this month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'nanos world', '13.1', '2,794', '213'],
                                ['2', 'Words On Stream', '11.6', '7,607', '656'],
                                ['3', 'Ragnarok X: Next Generation', '10.8', '551', '51'],
                                ['4', 'Scrabble', '10.7', '213', '20'],
                                ['5', 'Gartic on Stream', '10.4', '532', '51'],
                                ['6', 'Drawful 2', '10.4', '239', '23'],
                                ['7', 'The Playroom', '8.2', '181', '22'],
                                ['8', 'Dragon Warrior IV', '7.3', '190', '26'],
                                ['9', 'Game Room', '6.3', '1,078', '171'],
                                ['10', 'Mafia Online', '6.3', '171', '27'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            blocks: [
                p([
                    text('May had a smaller release slate with only 22 new party games, down from 46 in March and 37 in April. However, a high percentage of the new games had strong debuts on Twitch with four (and almost five!) releases clearing 100 streams compared with only two in prior months.'),
                ]),
            ],
            sections: [
                section({
                    id: 'new-releases__seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            em('The House Always Wins'),
                            text(' was the strongest streamer pickup: 502 streams from 319 streamers after its May 21 launch. The game is a 1-4 player co-op casino management sim, with players building the business, keeping slot machines running, and reacting to chaotic events like vandals and alien visitors.'),
                        ]),
                        p([
                            em('Bow-wow Battle'),
                            text(' had fewer streams but more audience per stream, helped by '),
                            link('https://twitch.tv/rubius', 'Rubius'),
                            text(' and a Spanish split that averaged 1,789 viewers. '),
                            em('Endix'),
                            text(', a fully digital gaming convention running as a game, charted on Twitch during its May 23-24 event.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['The House Always Wins', 'May 21', '502', '319', '144'],
                                ['Bow-wow Battle', 'May 1', '192', '183', '236'],
                                ['Endix', 'May 21', '165', '130', '43'],
                                ['Panic Delivery', 'May 13', '102', '93', '42'],
                                ['Stab!', 'May 1', '98', '84', '12'],
                                [
                                    'Nickelodeon Extreme Tennis: Next!',
                                    'May 28',
                                    '3',
                                    '3',
                                    '1',
                                ],
                                ['Outplay', 'May 24', '3', '3', '139'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('There were 15 other party games launched in May without observed Twitch activity. We list them below to bring awareness to these new games and help streamers discover something new.'),
                        list({
                            items: [
                                'Password Manager (May 1)',
                                'Blackspot: The Card Game (May 7)',
                                'Master Detective Reiji Takatori: Mystery Investigation (May 7)',
                                'Ecchi Crush (May 9)',
                                'Raccoon Survival Simulator (May 12)',
                                'A Playground Afternoon (May 14)',
                                'Amazing Facts: World Proverbs True or False Quiz (May 14)',
                                'Logic Training IQ Quiz for Kids (May 14)',
                                'Mushroom Simulator Co-op (May 15)',
                                'Playblades (May 16)',
                                'Pick & Place (May 21)',
                                'Crown Chaos (May 23)',
                                'Dramatour (May 28)',
                                'Touhou Yukkuri Mountain (May 28)',
                                'Nerds Revenge (May 31)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            sections: [
                section({
                    id: 'streamer-spotlight__most-active',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' led activity again with 211 captures, all on '),
                            em('Diablo II: Resurrected'),
                            text(', but their average viewers continue to consistently slide every month.'),
                        ]),
                        p([
                            link('https://twitch.tv/telinha', 'Telinha'),
                            text(' is the healthier high-frequency example. They were down from 200 to 175 captures, all on '),
                            em('Fall Guys'),
                            text(', but average viewers rose from 213 to 251. The rest of the top 10 is mostly single-game repetition: '),
                            link('https://twitch.tv/tygerladi', 'TygerLadi'),
                            text(' and '),
                            link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                            text(' on '),
                            em('Words On Stream'),
                            text(', '),
                            link('https://twitch.tv/xgeera', 'xGeera'),
                            text(' on Spanish '),
                            em('Gartic on Stream'),
                            text(', and '),
                            link('https://twitch.tv/cabgxaero', 'CABGxAero'),
                            text(' mostly on '),
                            em('Words On Stream'),
                            text(' with a small '),
                            em('Monopoly Plus'),
                            text(' side run.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '211',
                                    '74',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '211',
                                    '21',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '211',
                                    '10',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '209',
                                    '36',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/amiibots', 'Amiibots')],
                                    '196',
                                    '10',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '175',
                                    '251',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '175',
                                    '20',
                                    '1',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                    ],
                                    '158',
                                    '11',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/xgeera', 'xGeera')],
                                    '130',
                                    '86',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/cabgxaero', 'CABGxAero')],
                                    '115',
                                    '10',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 captures',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/kato_junichi0817', '加藤純一うん〇ちゃん'),
                            text(' topped the table with two '),
                            em('Unrailed!'),
                            text(' captures averaging 46,994 viewers. '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' was again drawing big audiences, this time returning with massive streams of '),
                            em('Among Us'),
                            text(' and '),
                            em('Golf With Your Friends'),
                            text('.'),
                        ]),
                        p([
                            text('Rounding out the top three was '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(', an Italian streamer worth a closer look. We caught them streaming party games six times across five games, including less-frequently streamed titles like '),
                            em('Family Feud'),
                            text(', '),
                            em('Frog Jump'),
                            text(', and '),
                            em('Monopoly Plus'),
                            text('. This one channel contributed to more than 80% of all Italian-language viewership for the month.'),
                        ]),
                        p([
                            link('https://twitch.tv/xqc', 'xQc'),
                            text(', known for gambling-related streams, split his party game time between '),
                            em("Liar's Bar"),
                            text(' and '),
                            em('The House Always Wins'),
                            text('. Newcomer to the list, '),
                            link('https://twitch.tv/lirik', 'LIRIK'),
                            text(', was seen playing the Japanese puzzle game '),
                            em('Suika Game'),
                            text(' three times.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Captures',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [
                                        link('https://twitch.tv/kato_junichi0817', '加藤純一うん〇ちゃん'),
                                    ],
                                    '46,994',
                                    '2',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '42,516',
                                    '2',
                                    '2',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                    '35,248',
                                    '6',
                                    '5',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/xqc', 'xQc')],
                                    '21,942',
                                    '2',
                                    '2',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/rdcgaming', 'RDCgaming')],
                                    '11,740',
                                    '3',
                                    '3',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/lirik', 'LIRIK')],
                                    '11,399',
                                    '3',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/yuyuta0702', 'ゆゆうた押忍')],
                                    '7,886',
                                    '5',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/jerma985', 'Jerma985')],
                                    '6,686',
                                    '4',
                                    '2',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/ironmouse', 'ironmouse')],
                                    '6,520',
                                    '2',
                                    '1',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/ludwig', 'Ludwig')],
                                    '6,240',
                                    '3',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__most-versatile',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(' led versatility for the fourth straight report, primarily rotating through retro games like '),
                            em('Mario Party 2'),
                            text(' and '),
                            em('Mario Party 3'),
                            text('.'),
                        ]),
                        p([
                            link('https://twitch.tv/cheesy', 'Cheesy'),
                            text(' hits the rankings for the first time, reaching #3 in versatility with 15 captures across nine party games, led by '),
                            em('Jackbox Party Packs'),
                            text(', '),
                            em('Among Us'),
                            text(', and '),
                            em('Flock Around'),
                            text('.'),
                        ]),
                        p([
                            link('https://twitch.tv/pietsmiettv', 'PietSmietTV'),
                            text(', '),
                            link('https://twitch.tv/xmarksthespot26', 'XmarkstheSpot26'),
                            text(', and '),
                            link('https://twitch.tv/chilledchaos', 'ChilledChaos'),
                            text(' each averaged large crowds of 200+ viewers on the 8+ party games they played during the month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Captures',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '16',
                                    '72',
                                    '17',
                                ],
                                [
                                    '2',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '10',
                                    '75',
                                    '70',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/cheesy', 'Cheesy')],
                                    '9',
                                    '15',
                                    '73',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/aq_noops', 'AQ_Noops')],
                                    '9',
                                    '12',
                                    '46',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '9',
                                    '11',
                                    '240',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/fnzyn_', 'fnzyn_')],
                                    '8',
                                    '16',
                                    '34',
                                ],
                                [
                                    '7',
                                    [
                                        link('https://twitch.tv/xmarksthespot26', 'XmarkstheSpot26'),
                                    ],
                                    '8',
                                    '13',
                                    '295',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/miguelo27xd', 'miguelo27xd')],
                                    '8',
                                    '13',
                                    '11',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/chilledchaos', 'ChilledChaos')],
                                    '8',
                                    '12',
                                    '3,191',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/yoshiphoenix', 'YoshiPhoenix')],
                                    '8',
                                    '12',
                                    '15',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            blocks: [
                p([
                    text('Party game streaming remained multilingual in May with 35 observed languages. English still made up most volume, but it also supplied the largest gross decline: 122,093 April streams to 116,380 May streams. Japanese, Portuguese, French, Italian, German, and Arabic were the largest stream-count gainers.'),
                ]),
                p([
                    text('The largest percent gains among languages with at least 100 April streams were Turkish, Arabic, Italian, Portuguese, and Japanese. The largest percent drops were Czech, Thai, Finnish, Hungarian, and Korean. The Italian average-viewer jump, 26 to 183, was almost entirely a '),
                    link('https://twitch.tv/tumblurr', 'Tumblurr'),
                    text(' effect: their six streams produced more than 200,000 viewers while the other 1,411 Italian streams combined for less than 50,000 viewers.'),
                ]),
                p([
                    text('Language mix also explains several game stories elsewhere in the report: French repeat streamers drove '),
                    em('nanos world'),
                    text(', Spanish audience concentrated around '),
                    em('Bow-wow Battle'),
                    text(', German channels mattered for '),
                    em('Scrabble'),
                    text(' and '),
                    em('Golf With Your Friends'),
                    text(', and Russian activity helped '),
                    em('Gartic Phone'),
                    text(' stay visible.'),
                ]),
            ],
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '116,380', '44,784', '16'],
                                ['2', 'Spanish', '15,783', '7,118', '15'],
                                ['3', 'French', '11,630', '4,185', '33'],
                                ['4', 'German', '9,461', '4,198', '33'],
                                ['5', 'Japanese', '9,282', '3,524', '34'],
                                ['6', 'Russian', '7,807', '3,467', '24'],
                                ['7', 'Portuguese', '5,207', '2,348', '31'],
                                ['8', 'Chinese', '3,266', '1,289', '34'],
                                ['9', 'Italian', '1,417', '659', '183'],
                                ['10', 'Polish', '883', '400', '45'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__en',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__en__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Super Smash Bros. Ultimate', '8,773', '2,336'],
                                        ['2', 'Fall Guys', '8,011', '3,519'],
                                        ['3', 'Among Us', '8,002', '4,637'],
                                        ['4', 'Lethal Company', '6,924', '3,896'],
                                        ['5', 'Words On Stream', '6,902', '567'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__en__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Monkey Ball', '907', '30'],
                                        ['2', 'Suika Game', '834', '44'],
                                        ['3', 'Casino', '413', '37'],
                                        ['4', 'Salamander County Public Television', '218', '7'],
                                        [
                                            '5',
                                            'SpongeBob SquarePants: Lights, Camera, Pants!',
                                            '155',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__en__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '211',
                                            '74',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '211',
                                            '21',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '211',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/mrpeanut1028', 'MrPeanut1028'),
                                            ],
                                            '210',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/visionz44', 'Visionz44')],
                                            '210',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__en__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/xqc', 'xQc')],
                                            '21,942',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/rdcgaming', 'RDCgaming')],
                                            '11,740',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/lirik', 'LIRIK')],
                                            '11,399',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/jerma985', 'Jerma985')],
                                            '6,686',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ironmouse', 'ironmouse')],
                                            '6,520',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__es',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__es__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Super Smash Bros. Ultimate', '2,053', '647'],
                                        ['2', 'Fall Guys', '1,974', '949'],
                                        ['3', 'Among Us', '1,581', '950'],
                                        ['4', 'Mario Kart World', '990', '333'],
                                        ['5', 'Gartic Phone', '897', '519'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__es__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Bow-wow Battle', '1,789', '15'],
                                        ['2', 'Backseat Drivers', '762', '5'],
                                        ['3', 'Super Bunny Man', '586', '29'],
                                        ['4', 'Wii Sports', '536', '15'],
                                        ['5', 'Endix', '122', '18'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__es__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/xgeera', 'xGeera')],
                                            '130',
                                            '86',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/gerardhetfield', 'gerardhetfield'),
                                            ],
                                            '74',
                                            '18',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vizard39', 'vizard39')],
                                            '54',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kuroshiwii', 'Kuroshiwii')],
                                            '53',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sliverrk', 'SliverRK')],
                                            '50',
                                            '162',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__es__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/agustinunaplay8', 'AgustinUnaplay8'),
                                            ],
                                            '3,632',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,387',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vegetta777', 'VEGETTA777')],
                                            '2,366',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mictia00', 'Mictia00')],
                                            '1,706',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '1,396',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__fr',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__fr__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '2,681', '202'],
                                        ['2', 'Among Us', '1,199', '675'],
                                        ['3', 'Super Smash Bros. Ultimate', '1,031', '294'],
                                        ['4', 'Mario Kart World', '850', '323'],
                                        ['5', 'Fall Guys', '847', '410'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__fr__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'MONOPOLY', '2,457', '7'],
                                        ['2', 'Maitre des Fleurs', '688', '23'],
                                        ['3', 'PlateUp!', '206', '41'],
                                        ['4', 'Lycans', '192', '197'],
                                        ['5', 'Free for Fall', '192', '7'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__fr__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '76',
                                            '79',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/walidoen17', 'walidoen17')],
                                            '59',
                                            '86',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '58',
                                            '23',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/wamoun', 'Wamoun')],
                                            '53',
                                            '136',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/adamchico_', 'AdamChico_')],
                                            '50',
                                            '47',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__fr__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '4,541',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/locklear', 'Locklear')],
                                            '3,875',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,742',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/sakor_', 'Sakor_')],
                                            '1,540',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ouaiseddy', 'OuaisEddy')],
                                            '1,375',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__de',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__de__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,690', '913'],
                                        ['2', 'Fall Guys', '917', '467'],
                                        ['3', 'Golf With Your Friends', '646', '402'],
                                        ['4', 'Mario Kart 8 Deluxe', '616', '282'],
                                        ['5', 'Super Smash Bros. Ultimate', '593', '146'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__de__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Scrabble', '349', '11'],
                                        ['2', 'Make Way', '166', '27'],
                                        ['3', 'Golf With Your Friends', '138', '646'],
                                        ['4', 'Super Battle Golf', '92', '105'],
                                        ['5', 'Monopoly Plus', '82', '17'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__de__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '75',
                                            '70',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/meloi_tv', 'Meloi_TV')],
                                            '50',
                                            '12',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/achraf_779', 'Achraf_779')],
                                            '37',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kiwin44', 'KiWin44')],
                                            '27',
                                            '15',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/drmarcel1', 'drmarcel1')],
                                            '27',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__de__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '42,516',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/krokoboss', 'krokoboss')],
                                            '5,074',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/xthesolutiontv', 'xTheSolutionTV'),
                                            ],
                                            '3,315',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dhalucard', 'Dhalucard')],
                                            '2,734',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kamikatze', 'Kamikatze')],
                                            '1,648',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__ja',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__ja__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '2,148', '761'],
                                        ['2', 'Super Smash Bros. Ultimate', '1,158', '247'],
                                        ['3', 'Beat Saber', '808', '170'],
                                        ['4', 'Among Us', '455', '318'],
                                        ['5', 'Mario Kart World', '393', '136'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__ja__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Rhythm Heaven Fever', '2,042', '16'],
                                        ['2', 'Wii Sports Resort', '880', '8'],
                                        ['3', 'Unrailed!', '789', '122'],
                                        ['4', 'Pummel Party', '322', '37'],
                                        ['5', 'The House Always Wins', '199', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__ja__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nohoho12', 'のほほ')],
                                            '42',
                                            '213',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/pokapokacan', 'ぽかぽかさん')],
                                            '42',
                                            '34',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sabomkw', 'さっぼさん')],
                                            '40',
                                            '13',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/marumarusya', '古野間マルシャ'),
                                            ],
                                            '39',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kai_shimotsuki', '霜月珈唯')],
                                            '32',
                                            '19',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__ja__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/kato_junichi0817', '加藤純一うん〇ちゃん'),
                                            ],
                                            '46,994',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/yuyuta0702', 'ゆゆうた押忍')],
                                            '7,886',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/mikogami_kotone', '御子神琴音'),
                                            ],
                                            '2,494',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/rei7_2434', 'Rei7_2434')],
                                            '2,400',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/hanjoudesu', 'はんじょう')],
                                            '2,380',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__ru',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__ru__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Gartic Phone', '1,324', '618'],
                                        ['2', 'Lethal Company', '895', '413'],
                                        ['3', 'Jackbox Party Packs', '781', '248'],
                                        ['4', 'Diablo II: Resurrected', '643', '99'],
                                        ['5', 'SIGame', '403', '297'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__ru__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Among Us 3D: VR', '827', '7'],
                                        ['2', 'Casino', '441', '16'],
                                        ['3', 'The House Always Wins', '283', '58'],
                                        ['4', 'Panic Delivery', '126', '9'],
                                        ['5', 'LEGO Star Wars III: The Clone Wars', '95', '6'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__ru__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '209',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/sashatechnic', 'SashaTechnic'),
                                            ],
                                            '49',
                                            '13',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/hc_fm', 'hc_fm')],
                                            '48',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kos_jv', 'Kos_JV')],
                                            '40',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mizarg2', 'mizarg2')],
                                            '38',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__ru__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '3,281',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/orkpod', 'orkpod')],
                                            '2,382',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/dangar', 'Dangar')],
                                            '1,422',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vika_karter', 'vika_karter')],
                                            '1,138',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/browjey', 'BrowJey')],
                                            '836',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__pt',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__pt__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '491', '177'],
                                        ['2', 'Words On Stream', '327', '46'],
                                        ['3', 'Stumble Guys', '281', '97'],
                                        ['4', 'Among Us', '274', '181'],
                                        ['5', 'Goose Goose Duck', '273', '123'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__pt__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Dale & Dawson Stationery Supplies', '333', '10'],
                                        ['2', 'UNO', '213', '22'],
                                        ['3', 'Bow-wow Battle', '171', '7'],
                                        ['4', 'Stick Fight: The Game', '123', '8'],
                                        ['5', 'Fall Guys', '101', '491'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__pt__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '175',
                                            '251',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/africaalbion', 'AfricaAlbion'),
                                            ],
                                            '110',
                                            '132',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/rafasocatristee', 'rafasocatristee'),
                                            ],
                                            '62',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/xxmarciano', 'XXMARCIANO')],
                                            '53',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/1vitin1', '1vitin1')],
                                            '46',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__pt__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/yagostooso', 'yagostooso')],
                                            '1,204',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/smzinho', 'Smzinho')],
                                            '886',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/gaules', 'Gaules')],
                                            '883',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/heitir', 'Heitir')],
                                            '714',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/zanfas_cenegal', 'Zanfas_Cenegal'),
                                            ],
                                            '707',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__zh',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__zh__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '958', '359'],
                                        ['2', 'Goose Goose Duck', '282', '183'],
                                        ['3', 'Diablo II: Resurrected', '220', '29'],
                                        ['4', 'Mabinogi', '200', '41'],
                                        ['5', 'Party Animals', '166', '100'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__zh__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'The House Always Wins', '1,087', '6'],
                                        ['2', 'Jinsei Game for Nintendo Switch', '302', '8'],
                                        ['3', 'KLETKA', '215', '50'],
                                        ['4', 'Super Bunny Man', '109', '12'],
                                        ['5', 'One Hand Clapping', '99', '9'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__zh__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/news86', 'News86')],
                                            '28',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/slmtenno2025', '史萊姆的第二個家'),
                                            ],
                                            '20',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/11ee11', '伊伊是伊伊')],
                                            '19',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/raven_ouo', '渡夜爵')],
                                            '18',
                                            '11',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/takjj', '風間徹')],
                                            '17',
                                            '26',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__zh__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mobilmobil', '老皮')],
                                            '5,281',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kr1stw', '西南69')],
                                            '4,206',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/bbbb87', '世誠')],
                                            '2,960',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '2,633',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/guanweiboy', '冠緯')],
                                            '2,280',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__it',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__it__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '315', '136'],
                                        ['2', 'Among Us', '150', '78'],
                                        ['3', 'Super Smash Bros. Ultimate', '86', '34'],
                                        ['4', 'Golf With Your Friends', '81', '50'],
                                        ['5', 'Mario Kart World', '67', '30'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__it__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '3,892', '22'],
                                        ['2', 'Fall Guys', '119', '315'],
                                        ['3', 'Live Bingo', '91', '8'],
                                        ['4', 'Just Dance 2026 Edition', '78', '21'],
                                        ['5', 'Panic Delivery', '73', '6'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__it__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/baldonetv', 'BaldoneTV')],
                                            '22',
                                            '106',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '20',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/nonnogamer70', 'NonnoGamer70'),
                                            ],
                                            '19',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '18',
                                            '182',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/j_papu_03', 'J_Papu_03')],
                                            '14',
                                            '37',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__it__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                            '35,248',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '2,917',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/manuuxo', 'ManuuXO')],
                                            '2,864',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/giankoextreme', 'giankoextreme'),
                                            ],
                                            '960',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/pesh', 'Pesh')],
                                            '462',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__pl',
                    title: 'Polish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__pl__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '228', '37'],
                                        ['2', 'Among Us', '110', '65'],
                                        ['3', 'Fall Guys', '105', '33'],
                                        ['4', 'Lethal Company', '70', '28'],
                                        ['5', 'Gartic Phone', '42', '39'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__pl__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Gartic Phone', '519', '42'],
                                        ['2', 'Whatsamusic', '212', '13'],
                                        ['3', 'Among Us', '71', '110'],
                                        ['4', 'Keep Talking and Nobody Explodes', '39', '10'],
                                        ['5', 'Mario Kart World', '35', '11'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__pl__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '28',
                                            '28',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/duzylosiu', 'duzylosiu')],
                                            '10',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/gacus1221', 'GacuS1221')],
                                            '9',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kuzyn94_', 'Kuzyn94_')],
                                            '7',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/eybiak', 'Eybiak')],
                                            '6',
                                            '46',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__pl__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/graf', 'Graf')],
                                            '2,829',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tyrisftw', 'tyrisftw')],
                                            '772',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kasia_22', 'Kasia_22')],
                                            '274',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/snakeiter1', 'Snakeiter1')],
                                            '84',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/gugagamerka', 'GugaGamerka')],
                                            '68',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            blocks: [
                p('The game deep dives show who created repeat activity and who supplied the biggest audience moments for the top Party Score games. They are most useful for distinguishing broad game health from a small number of high-viewer streams.'),
            ],
            sections: [
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('2,794 streams | 213 streamers | 13.1 stickiness | 56 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '76',
                                            '79',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/walidoen17', 'walidoen17')],
                                            '59',
                                            '86',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '58',
                                            '23',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/wamoun', 'Wamoun')],
                                            '53',
                                            '136',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/adamchico_', 'AdamChico_')],
                                            '50',
                                            '47',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/ouaiseddy', 'OuaisEddy')],
                                            '1,375',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/qassimiento', 'Qassimiento')],
                                            '1,176',
                                            '16',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '1,028',
                                            '37',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '573',
                                            '29',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/hamzakerdaloco', 'HamzaKerdaloco'),
                                            ],
                                            '377',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('7,607 streams | 656 streamers | 11.6 stickiness | 19 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '211',
                                            '10',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '209',
                                            '36',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mamabunnz', 'mamabunnz')],
                                            '184',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '175',
                                            '20',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '158',
                                            '11',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/roxburgh', 'Roxburgh')],
                                            '1,424',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/piapiufo', 'PiaPiUFO')],
                                            '522',
                                            '42',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/notdebpark', 'notdebpark')],
                                            '304',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/skinnedteen', 'skinnedteen')],
                                            '236',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/buni', 'Buni')],
                                            '225',
                                            '53',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('663 streams | 247 streamers | 2.7 stickiness | 58 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dellamaurie', 'dellAMAURIE')],
                                            '28',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/floridawoman', 'floridawoman'),
                                            ],
                                            '17',
                                            '44',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ulises_eric', 'Ulises_Eric')],
                                            '17',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/danceshayadance', 'DanceShayaDance'),
                                            ],
                                            '14',
                                            '16',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/ratatoscr', 'RATATOscr')],
                                            '12',
                                            '28',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,492',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/littlesiha', 'littlesiha')],
                                            '2,160',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,443',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/fems', 'Fems')],
                                            '231',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sophelyy', 'Sophelyy')],
                                            '218',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('13,901 streams | 3,763 streamers | 3.7 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '211',
                                            '21',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/amiibots', 'Amiibots')],
                                            '196',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/metalvayne08', 'metalvayne08'),
                                            ],
                                            '75',
                                            '40',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/afk_host', 'AFK_Host')],
                                            '41',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/cirrentv', 'CirrenTV')],
                                            '41',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sakor_', 'Sakor_')],
                                            '1,540',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lowtiergod', 'LowTierGod')],
                                            '1,317',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                            '1,231',
                                            '29',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '1,001',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/eastgeeksmash', 'eastgeeksmash'),
                                            ],
                                            '589',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('8,913 streams | 1,576 streamers | 5.7 stickiness | 17 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '211',
                                            '74',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/alexislegit', 'AlexIsLegit')],
                                            '107',
                                            '28',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '74',
                                            '15',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/realw986', 'RealW986')],
                                            '59',
                                            '34',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/thatdudewillyd', 'thatdudewillyd'),
                                            ],
                                            '57',
                                            '56',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jerma985', 'Jerma985')],
                                            '6,487',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/datmodz', 'DatModz')],
                                            '1,685',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mathil1', 'Mathil1')],
                                            '1,006',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '592',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mr_mammal', 'Mr_Mammal')],
                                            '460',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__fall-guys',
                    title: 'Fall Guys',
                    collapsible: true,
                    blocks: [
                        p('13,654 streams | 6,092 streamers | 2.2 stickiness | 20 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '175',
                                            '251',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/salad_enjoyerr', 'Salad_Enjoyerr'),
                                            ],
                                            '103',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/creshta', 'creshta')],
                                            '85',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/gerardhetfield', 'gerardhetfield'),
                                            ],
                                            '74',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/chaofanj', 'ChaofanJ')],
                                            '60',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,246',
                                            '5',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/fivfiv001', 'fivfiv001')],
                                            '674',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/aplatypuss', 'APlatypuss')],
                                            '636',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/anthonydaytv', 'AnthonyDayTV'),
                                            ],
                                            '472',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lightorias', 'Lightorias')],
                                            '306',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-64',
                    title: 'Mario Kart 64',
                    collapsible: true,
                    blocks: [
                        p('274 streams | 109 streamers | 2.5 stickiness | 42 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-64__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/abney317', 'abney317')],
                                            '25',
                                            '310',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mk64mr', 'MK64MR')],
                                            '19',
                                            '64',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/grey0__', 'grey0__')],
                                            '16',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/thegrandegamerttv', 'TheGrandeGamerTTV'),
                                            ],
                                            '12',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/braktheman', 'braktheman')],
                                            '9',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-64__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/raysfire', 'raysfire')],
                                            '414',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/abney317', 'abney317')],
                                            '310',
                                            '25',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mk64mr', 'MK64MR')],
                                            '64',
                                            '19',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/luckyvulp', 'LuckyVulp')],
                                            '25',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/grey0__', 'grey0__')],
                                            '17',
                                            '16',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-world',
                    title: 'Mario Kart World',
                    collapsible: true,
                    blocks: [
                        p('5,817 streams | 2,321 streamers | 2.5 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/vantox_', 'Vantox_')],
                                            '47',
                                            '68',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sliverrk', 'SliverRK')],
                                            '43',
                                            '167',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/worldfriendtv1', 'Worldfriendtv1'),
                                            ],
                                            '35',
                                            '31',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/greezyszn', 'Greezyszn')],
                                            '31',
                                            '12',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aketxz', 'Aketxz')],
                                            '27',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tkch_taisyou', 'タイショワ')],
                                            '1,028',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '639',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/seereaxtv', 'SeeReaxTV')],
                                            '628',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/frivvifox', 'FrivviFox')],
                                            '412',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/gamescage', 'GamesCage')],
                                            '400',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__jackbox-party-packs',
                    title: 'Jackbox Party Packs',
                    collapsible: true,
                    blocks: [
                        p('4,479 streams | 2,064 streamers | 2.2 stickiness | 19 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__jackbox-party-packs__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '209',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/youplaylive', 'youplaylive')],
                                            '177',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/chaotik_lord', 'chaotik_lord'),
                                            ],
                                            '36',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/mattitude1233', 'Mattitude1233'),
                                            ],
                                            '30',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/bunnyfett', 'BunnyFett')],
                                            '28',
                                            '28',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__jackbox-party-packs__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/chilledchaos', 'ChilledChaos'),
                                            ],
                                            '3,242',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/dooleynoted', 'DooleyNoted')],
                                            '946',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/jacksfilms', 'jacksfilms')],
                                            '632',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/skaifox', 'Skaifox')],
                                            '612',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/jocat', 'JoCat')],
                                            '563',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('4,306 streams | 1,541 streamers | 2.8 stickiness | 17 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '43',
                                            '32',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mizarg2', 'mizarg2')],
                                            '38',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/raisinnnnnn', 'raisinnnnnn')],
                                            '35',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/gomashiojijii', 'ごましおじじい'),
                                            ],
                                            '30',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/knobara', '釘崎くぎみん')],
                                            '27',
                                            '55',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '2,633',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mimiluckying', '獅子丸')],
                                            '1,295',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kokujintv', 'kokujintv')],
                                            '1,255',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/dingding248', '丁義')],
                                            '631',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/32mkll', 'みつこ')],
                                            '396',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            sections: [
                section({
                    id: 'streaming-patterns__day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p('The weekly shape stayed familiar, with Saturday and Sunday still the busiest average stream days, but every day was down from April with the steepest drops on Saturday, Tuesday, and Sunday. The reason streamers are staying on the sidelines remains to be seen: while it could be waning interest in games like Super Battle Golf, there may also be a seasonal shift as people get busier with IRL activities as summer approaches in the northern hemisphere.'),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '1,026', '19'],
                                ['Monday', '770', '22'],
                                ['Tuesday', '722', '23'],
                                ['Wednesday', '756', '22'],
                                ['Thursday', '776', '30'],
                                ['Friday', '925', '18'],
                                ['Saturday', '1,088', '21'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p("The hour profile still clustered around 00:00-04:00 UTC and 18:00-23:00 UTC even though the biggest losses in May were around those same peak times, between 23:00-02:00 UTC and 19:00-20:00 UTC. Those are exactly the broad North American evening and European prime-time windows where one-off creator sampling would show up. While we don't yet have the historical data to definitely say, the peak time pattern does suggest a shift to IRL activities as summer approaches in the northern hemisphere. The June report should help confirm or reject this hypothesis."),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,215', '24'],
                                ['01:00', '1,334', '16'],
                                ['02:00', '1,316', '16'],
                                ['03:00', '1,211', '17'],
                                ['04:00', '1,113', '15'],
                                ['05:00', '879', '17'],
                                ['06:00', '681', '16'],
                                ['07:00', '573', '16'],
                                ['08:00', '445', '17'],
                                ['09:00', '421', '20'],
                                ['10:00', '375', '21'],
                                ['11:00', '469', '22'],
                                ['12:00', '550', '37'],
                                ['13:00', '648', '18'],
                                ['14:00', '712', '27'],
                                ['15:00', '748', '18'],
                                ['16:00', '800', '23'],
                                ['17:00', '868', '22'],
                                ['18:00', '1,118', '23'],
                                ['19:00', '1,187', '24'],
                                ['20:00', '1,228', '29'],
                                ['21:00', '1,141', '27'],
                                ['22:00', '1,123', '24'],
                                ['23:00', '1,106', '25'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-05-top-party-games-and-streamers.js.map